import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Light from './headers/light';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Footerr from './footers/Footer';
import ScrollToTop from './ScrollToTop';

const CheckoutForm = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem('authToken');
    const id = localStorage.getItem('id');
    const role = localStorage.getItem('role');  // Assuming the role is saved in localStorage
    const navigate = useNavigate();
    const [cart, setCart] = useState([]);
    const [codePromo, setCodePromo] = useState('');
    const [discount, setDiscount] = useState(0);
    const [message, setMessage] = useState(''); // used for promo feedback
    const [customer, setCustomer] = useState({
        Name: '',
        Email: '',
        Phone: '',
        Address: '',
        city: '',
        notes: '',
    });
    
    // --- LOGIQUE FIDÉLITÉ : Extraction du custom_id ---
    const userStr = localStorage.getItem("user");
    const userData = userStr ? JSON.parse(userStr) : null;
    const userCustomId = userData ? userData.custom_id : null;
    const [loyaltyPoints, setLoyaltyPoints] = useState(0); 

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dynamic cart subtotal helper function
    const calculateSubtotal = () => {
        return cart.reduce((total, item) => total + item.productPrice * item.quantity, 0);
    };

    useEffect(() => {
        const storedCart = JSON.parse(localStorage.getItem("cart")) || [];
        setCart(storedCart);

        if (role === "client_fidele" && userCustomId) {
            axios.get(`https://plateformewmg.cloud/api/loyalty/${userCustomId}`)
                .then(res => {
                    setLoyaltyPoints(res.data.points || 0);
                    if (res.data.points > 0) {
                        setCodePromo(userCustomId);
                    }
                })
                .catch(err => console.error("Erreur points de fidélité:", err));
        }
    }, [role, userCustomId]);

    useEffect(() => {
        const storedCart = JSON.parse(localStorage.getItem('cart')) || [];
        setCart(storedCart);
    }, [token, role, navigate]);

    // Handle customer input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const nextValue = name === 'notes' ? value.slice(0, 120) : value;
        setCustomer((prev) => ({ ...prev, [name]: nextValue }));
    };

    // Handle quantity change
    const handleQuantityChange = (productID, newQuantity) => {
        if (newQuantity < 1) return;

        const updatedCart = cart.map(item =>
            item.productID === productID ? { ...item, quantity: newQuantity } : item
        );
        setCart(updatedCart);
        localStorage.setItem('cart', JSON.stringify(updatedCart));
    };

    // ✅ Handle item removal
    const handleRemoveItem = (productID) => {
        const updatedCart = cart.filter(item => item.productID !== productID);
        setCart(updatedCart);
        localStorage.setItem('cart', JSON.stringify(updatedCart));
    };

    // Quantity stepper
    const handleQuantityStep = (productID, delta) => {
        const item = cart.find(i => i.productID === productID);
        if (!item) return;
        const nextQty = Math.max(1, (item.quantity || 1) + delta);
        handleQuantityChange(productID, nextQty);
    };

    const getFinalTotal = () => {
        const subtotal = calculateSubtotal();

        if (role === "client_fidele" && loyaltyPoints > 0) {
            const reduction = loyaltyPoints * 10;
            const final = subtotal - reduction;
            return final > 0 ? final : 0;
        }

        if (discount > 0) return discount;

        return subtotal;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true); // ✅ bouton devient "en cours..." + désactivé
        if (!/^0[5-7][0-9]{8}$/.test(customer.Phone)) {
            Swal.fire('Erreur', 'Le numéro de téléphone doit commencer par 0 et contenir 10 chiffres valides du Maroc.', 'error');
            setIsSubmitting(false); // Reset the button state
            return;
        }

        const sanitizedNote = (customer.notes || '').trim().slice(0, 120);
        let supplierId = id;
        if (codePromo) {
            try {
                const response = await axios.get(`https://plateformewmg.cloud/api/supplier-from-code/${codePromo}`);
                supplierId = response.data.supplier_id;
            } catch (error) {
                Swal.fire('Erreur', t('Code_promo_invalide'), 'error');
                setIsSubmitting(false);
                return;
            }
        }
        const customerWithSupplier = {
            ...customer,
            notes: sanitizedNote,
            ...(supplierId && { SupplierID: supplierId }), // Only include it if supplierId is NOT null
        };
        try {
            // Create the customer
            const customerResponse = await axios.post('https://plateformewmg.cloud/api/customers/create', customerWithSupplier, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }), // optional if logged in
                },
            });

            const customerId = customerResponse.data.customer.CustomerID;

            // Create the order
            const orderData = {
                product_ids: cart.map(item => item.productID),
                prices: cart.map(item => item.productPrice),
                priceGain: cart.map(item => item.priceGain),// <== Add this
                quantities: cart.map(item => item.quantity),
                customer_id: customerId,
                promo_code: codePromo,
                supplierId: id,
                notes: sanitizedNote,
            };

            const orderResp = await axios.post('https://plateformewmg.cloud/api/orders/create', orderData, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }), // optional if logged in
                },
            });

            // Extract identifiers from API response
            const { idsOrder, payment_id, supplier_id, message } = orderResp?.data || {};

            // Persist for refresh and clear cart
            sessionStorage.setItem('order_confirmation', JSON.stringify({ idsOrder, payment_id, supplier_id, message }));
            localStorage.removeItem('cart');

            setIsSubmitting(false);
            // Navigate to confirmation page with state
            navigate('/order-confirmation', { state: { idsOrder, payment_id, supplier_id, message } });

        } catch (error) {
            console.log(error);
            setIsSubmitting(false); // ✅ Reset
            if (error.response) {
                if (error.response.data.error === 'Your account has been deleted.') {
                    Swal.fire('Erreur!', 'Votre compte a été supprimé.', 'error');
                    localStorage.clear();
                    navigate('/signup'); 
                } else {
                    Swal.fire('Erreur!', error.response.data.message || 'Il y a eu un probléme, veuillez réessayer.', 'error');
                }
            } else if (error.request) {
                console.error("Request data:", error.request);
                Swal.fire('Erreur!', 'Probléme de connexion réseau, veuillez réessayer.', 'error');
            } else {
                console.error("Error details:", error);
                Swal.fire('Erreur!', 'Il y a eu un probléme, veuillez réessayer.', 'error');
            }
        }
    };

    const handlePromoCodeApply = async () => {
        if (!codePromo) {
            setMessage(t('Entrer_code_promo')); 
            return;
        }
        try {
            const response = await axios.get(`https://plateformewmg.cloud/api/supplier-from-code/${codePromo}`);
            if (response.data) {
                const discountRate = 0.15; // 15%
                const totalPrice = calculateSubtotal();
                const discountAmount = discountRate * totalPrice;
                const finalPrice = parseFloat((totalPrice - discountAmount).toFixed(2));
                setDiscount(finalPrice);

                setMessage(t('Code_promo_succes'));
            } else {
                setMessage(t('Code_promo_invalide'));
            }
        } catch (error) {
            setMessage(t('Code_promo_invalide'));
        }
    };

    const getShippingFee = () => {
        const subtotal = discount > 0 ? discount : calculateSubtotal();
        return subtotal >= 300 ? 0 : 20;
    };

    console.log("role", role);
    return (
        <AnimationRevealPage>
            <ScrollToTop />
            <Light />

            {/* Top banner (full-bleed) */}
            <div
                className="py-4 mb-4"
                style={{
                    backgroundColor: '#f5f7fb',
                    width: '100vw',
                    marginLeft: 'calc(50% - 50vw)',
                    marginRight: 'calc(50% - 50vw)'
                }}
            >
                <div className="container text-center">
                    <h3 className="mb-1" style={{ color: '#1f2937', fontWeight: 700, fontSize: 24 }}>{t('Shopping_Cart') || 'Shopping Cart'}</h3>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-0 justify-content-center">
                            <li className="breadcrumb-item"><a href="/">Home</a></li>
                            <li className="breadcrumb-item active" aria-current="page">{t('Shopping_Cart') || 'Shopping Cart'}</li>
                        </ol>
                    </nav>
                </div>
            </div>
            
            <div className="container">
                <div className="row">
                    {/* Left: cart table */}
                    <div className="col-xl-8 mb-4">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-0">
                                {cart.length > 0 ? (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table mb-0 align-middle">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th style={{ width: 40 }}></th>
                                                        <th>{t('Produits') || 'Products'}</th>
                                                        <th className="text-end">{t('Prix') || 'Price'}</th>
                                                        <th className="text-center">{t('quantity') || 'Quantity'}</th>
                                                        <th className="text-end">{t('Sous_total') || 'Subtotal'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cart.map((item) => (
                                                        <tr key={item.productID}>
                                                            <td className="text-muted">
                                                                <button onClick={() => handleRemoveItem(item.productID)} className="btn btn-sm btn-link text-muted p-0" title="Remove">×</button>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <img src={`https://plateformewmg.cloud/storage/${item.productImage}`} alt="" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', marginRight: 12 }} />
                                                                    <div className="fw-semibold text-dark">{item.productName}</div>
                                                                </div>
                                                            </td>
                                                            <td className="text-end">{Number(item.productPrice).toFixed(2)} {t('currency')}</td>
                                                            <td>
                                                                <div className="d-flex justify-content-center align-items-center">
                                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleQuantityStep(item.productID, -1)}>-</button>
                                                                    <input type="text" readOnly className="form-control text-center mx-2" value={item.quantity} style={{ width: 56 }} />
                                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleQuantityStep(item.productID, 1)}>+</button>
                                                                </div>
                                                            </td>
                                                            <td className="text-end">{(item.productPrice * item.quantity).toFixed(2)} {t('currency')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Coupon + actions bar */}
                                        <div className="d-flex flex-column flex-md-row gap-2 p-3 border-top">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder={t('Coupon_Code') || 'Coupon Code'}
                                                value={codePromo}
                                                onChange={(e) => setCodePromo(e.target.value)}
                                                style={{ maxWidth: 320 }}
                                            />
                                            <button className="btn" onClick={handlePromoCodeApply} style={{ backgroundColor: '#0f766e', color:'#fff' }}>{t('Appliquer_code_promo')}</button>
                                            <button className="btn btn-outline-secondary ms-md-auto">{t('Update_Cart') || 'Update Cart'}</button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="p-4">{t('Panier_vide')}.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right: summary card */}
                    <div className="col-xl-4">
                        <div className="mt-5 mt-lg-0">
                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-transparent border-bottom py-3 px-4">
                                    <h5 className="font-size-16 mb-0">{t('Resume_commande')}</h5>
                                </div>
                                <div className="card-body p-4 pt-2">
                                    {role === "client_fidele" && (
                                        <div className="alert alert-success p-2 small">
                                            <i className="fa fa-gift me-2"></i>
                                            <strong>Fidélité :</strong> Vous avez {loyaltyPoints} points.
                                            <br />
                                            Remise appliquée : <strong>-{loyaltyPoints * 10} DH</strong>
                                        </div>
                                    )}

                                    {role !== "client_fidele" && (
                                        <div className="mb-3">
                                            <input type="text" placeholder={t("Code_promo")} className="form-control mb-2" onChange={(e) => setCodePromo(e.target.value)} />
                                            <button type="button" onClick={handlePromoCodeApply} className="btn btn-sm w-100" style={{ backgroundColor: "#4e7662", color: "#fff" }}>
                                                {t("Appliquer_code_promo")}
                                            </button>
                                            {message && <p className="small mt-1 text-center text-primary">{message}</p>}
                                        </div>
                                    )}

                                    <div className="table-responsive">
                                        <table className="table table-borderless mb-0">
                                            <tbody>
                                                <tr>
                                                    <td>{t("Sous_total")}</td>
                                                    <td className="text-end">{calculateSubtotal().toFixed(2)} DH</td>
                                                </tr>
                                                {role === "client_fidele" && loyaltyPoints > 0 && (
                                                    <tr>
                                                        <td className="text-success">Remise Fidélité</td>
                                                        <td className="text-end text-success">-{loyaltyPoints * 10} DH</td>
                                                    </tr>
                                                )}
                                                {discount > 0 && role !== "client_fidele" && (
                                                    <tr>
                                                        <td className="text-primary">Remise Code</td>
                                                        <td className="text-end text-primary">-{ (calculateSubtotal() - discount).toFixed(2) } DH</td>
                                                    </tr>
                                                )}
                                                <tr className="border-top">
                                                    <th className="pt-2">{t("Total")}</th>
                                                    <th className="text-end pt-2" style={{ color: "#4e7662", fontSize: "1.2rem" }}>
                                                        {getFinalTotal().toFixed(2)} DH
                                                    </th>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <button
                                        className="btn w-100 mt-3"
                                        style={{ backgroundColor: '#0ea5b3', color: '#fff' }}
                                        onClick={() => document.getElementById('customer-form')?.scrollIntoView({behavior:'smooth'})}
                                    >
                                        {t('Proceed_To_Checkout') || 'Proceed To Checkout'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Customer info form (checkout step) */}
            <div className="container mt-4" id="customer-form">
                {cart.length > 0 && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h5 className="mb-3">{t('Informations_client')}</h5>
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <input type="text" name="Name" placeholder={t('Nom')} className="form-control" onChange={handleInputChange} required />
                                    </div>
                                    <div className="col-md-3">
                                        <input type="text" name="Phone" placeholder={t('Numéro_de_Téléphone')} className="form-control" onChange={handleInputChange} required />
                                    </div>
                                    <div className="col-md-3">
                                        <input type="text" name="Address" placeholder={t('Adresse')} className="form-control" onChange={handleInputChange} required />
                                    </div>
                                    <div className="col-md-3">
                                        <input type="text" name="City" placeholder={t('Ville')} className="form-control" onChange={handleInputChange} required />
                                    </div>
                                    <div className="col-12">
                                        <textarea
                                            name="notes"
                                            placeholder={t('note')}
                                            className="form-control"
                                            rows={3}
                                            maxLength={120}
                                            value={customer.notes}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <button
                                        type="submit"
                                        className="btn"
                                        disabled={isSubmitting}
                                        style={{
                                            backgroundColor: '#133b8c',
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px 20px',
                                            fontSize: '16px',
                                            borderRadius: '5px',
                                            fontWeight: 'bold',
                                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                            opacity: isSubmitting ? 0.7 : 1
                                        }}
                                    >
                                        {isSubmitting ? t('En_cours') + '...' : t('Confirmer_commande')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>

            <Footerr />
        </AnimationRevealPage>
    );
};

export default CheckoutForm;